import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogModule as MatDialogModule,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import {
  MatLegacySnackBar as MatSnackBar,
  MatLegacySnackBarModule as MatSnackBarModule,
} from '@angular/material/legacy-snack-bar';
import { BehaviorSubject, Observable } from 'rxjs';

import { ISalesOrder, ISalesOrderItem } from 'v2/modules/order';

import { OrderApiService } from '../../../../shared/services/api/order/order-api.service';
import { CreateInvoiceAgGridTableComponent } from '../create-invoice-ag-grid-table/create-invoice-ag-grid-table.component';
import { ISalesOrderFormInvoiceItem } from '../sales-order-invoice.interface';

@Component({
  selector: 'lum-create-invoice-from-sales-order-form',
  standalone: true,
  imports: [
    CommonModule,
    MatSnackBarModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    AsyncPipe,
    CreateInvoiceAgGridTableComponent,
  ],
  templateUrl: './create-invoice-from-sales-order-form.component.html',
  styleUrls: ['./create-invoice-from-sales-order-form.component.scss'],
})
export class CreateInvoiceFromSalesOrderFormComponent implements OnInit {
  _isLoading :BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  _totalQuantity :BehaviorSubject<number> = new BehaviorSubject<number>(0);

  @ViewChild(CreateInvoiceAgGridTableComponent)
  lineItemsAgGridTable! :CreateInvoiceAgGridTableComponent;

  salesOrder :ISalesOrder | null = null;
  salesOrderFormLineItems :ISalesOrderFormInvoiceItem[] = [];

  constructor(
    public dialogRef :MatDialogRef<CreateInvoiceFromSalesOrderFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data :{ salesOrderId :number },
    private orderApiService :OrderApiService,
    private snackBar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.getSalesOrder();
  }

  onQuantityToInvoiceUpdated(totalQuantity :number) :void {
    if (totalQuantity < 0) {
      this.snackBar.open('Quantity to invoice cannot be negative', 'Close', {
        duration: 2000,
      });
    }
    this._totalQuantity.next(totalQuantity);
  }

  private getSalesOrder() :void {
    const relations :string[] = [
      'orderItems',
      'orderItems.rfq',
      'orderItems.orderedUom',
    ];
    this._isLoading.next(true);
    this.orderApiService
      .getById(this.data.salesOrderId, { relations: relations.join(',') })
      .subscribe({
        next: (salesOrderData :ISalesOrder) => {
          this._isLoading.next(false);
          this.salesOrder = salesOrderData;
          this.salesOrderFormLineItems = salesOrderData.orderItems.map(
            (orderItem :ISalesOrderItem) => {
              const invoicedQuantity :number = Number(
                orderItem.quantityInvoiced || 0,
              );
              const quantity :number = Number(orderItem.quantity);
              const fulfilledQuantity :number = Number(
                orderItem.quantityShipped || 0,
              );
              let toInvoiceQuantity :number = fulfilledQuantity - invoicedQuantity;
              if (toInvoiceQuantity < 0) {
                toInvoiceQuantity = 0;
              }

              return {
                id: orderItem.id,
                productId: orderItem.rfqId,
                productName: orderItem.rfq?.productName,
                productSku: orderItem.rfq?.internalSku,
                orderedQuantity: quantity,
                orderedUnit: orderItem.orderedUom?.name || '',
                orderedUomId: orderItem.orderedUomId,
                invoicedQuantity: invoicedQuantity,
                fulfilledQuantity: fulfilledQuantity,
                toInvoiceQuantity: toInvoiceQuantity,
              };
            },
          );
          this.recomputeTotalQuantity();
        },
      });
  }

  onSetFulfilled() :void {
    this.salesOrderFormLineItems = this.salesOrderFormLineItems.map(
      (item :ISalesOrderFormInvoiceItem) => {
        let toInvoiceQuantity :number =
          item.fulfilledQuantity - item.invoicedQuantity;
        if (toInvoiceQuantity < 0) {
          toInvoiceQuantity = 0;
        }

        return {
          ...item,
          toInvoiceQuantity: toInvoiceQuantity,
        };
      },
    );
    this.recomputeTotalQuantity();
  }

  onSetOrdered() :void {
    this.salesOrderFormLineItems = this.salesOrderFormLineItems.map(
      (item :ISalesOrderFormInvoiceItem) => {
        return {
          ...item,
          toInvoiceQuantity: item.orderedQuantity - item.invoicedQuantity,
        };
      },
    );
    this.recomputeTotalQuantity();
  }

  recomputeTotalQuantity() :void {
    const totalQuantity :number = this.salesOrderFormLineItems.reduce(
      (quantityAccumulator, item) => {
        return item.productId && item.orderedUnit
          ? quantityAccumulator + item.toInvoiceQuantity
          : quantityAccumulator;
      },
      0,
    );
    this._totalQuantity.next(totalQuantity);
  }

  onSave() :void {
    const lineItemsData :ISalesOrderFormInvoiceItem[] =
      this.lineItemsAgGridTable.getLineItemsData();

    const totalInvoicedQUantity :number = lineItemsData.reduce(
      (quantityAccumulator, item) => {
        return item.productId && item.orderedUnit
          ? quantityAccumulator + item.toInvoiceQuantity
          : quantityAccumulator;
      },
      0,
    );
    if (totalInvoicedQUantity === 0) {
      this.snackBar.open('Please invoice at least one item', 'Close', {
        duration: 2000,
      });

      return;
    }

    const invoiceData :{
      shipstationOrderLineItemId :number;
      invoicedQuantity :number;
    }[] = lineItemsData
      .filter((item :ISalesOrderFormInvoiceItem) => {
        return item.productId && item.orderedUnit && item.toInvoiceQuantity > 0;
      })
      .map((item :ISalesOrderFormInvoiceItem) => {
        return {
          shipstationOrderLineItemId: item.id,
          invoicedQuantity: item.toInvoiceQuantity,
        };
      });

    this._isLoading.next(true);

    this.orderApiService
      .createPartialInvoice(this.salesOrder?.id || 0, invoiceData)
      .subscribe({
        next: () => {
          this.snackBar.open('Invoice created successfully', 'Close', {
            duration: 2000,
          });
          this.onClose();
        },
        error: (error :any) => {
          this._isLoading.next(false);
          this.snackBar.open('Failed to create invoice', 'Close', {
            duration: 2000,
          });
        },
      });
  }

  onClose() :void {
    this.dialogRef.close();
  }

  get isLoading$() :Observable<boolean> {
    return this._isLoading.asObservable();
  }

  get totalQuantity$() :Observable<number> {
    return this._totalQuantity.asObservable();
  }
}
